/**
 * Centralized error-to-UX mapping for invitation errors
 * Translates backend error codes into user-friendly messages and actions
 */

export interface InviteError {
  code: string;
  message: string;
  meta?: Record<string, any>;
}

export interface InviteErrorUI {
  title: string;
  description: string;
  variant: 'default' | 'destructive' | 'warning';
  action?: 'RESEND' | 'VIEW_MEMBERS' | 'CREATE_NEW' | 'RETRY' | 'REQUEST_ACCESS' | 'JOIN_WORKSPACE';
  actionLabel?: string;
  meta?: Record<string, any>;
}

/**
 * Maps backend error codes to user-friendly UI messages and actions
 */
export function handleInviteError(error: any): InviteErrorUI {
  // Handle structured error response from Edge Function
  const errorCode = error?.code || error?.error?.code || 'UNKNOWN_ERROR';
  const errorMessage = error?.message || error?.error?.message || 'Something went wrong';
  const errorMeta = error?.meta || error?.error?.meta || {};

  switch (errorCode) {
    case 'INVITE_ALREADY_SENT':
      return {
        title: 'Invitation already sent',
        description: 'An invitation has already been sent to this email address.',
        variant: 'warning',
        action: 'RESEND',
        actionLabel: 'Resend Invitation',
        meta: errorMeta,
      };

    case 'USER_ALREADY_MEMBER':
      return {
        title: 'User already in workspace',
        description: 'This user is already a member of the workspace.',
        variant: 'warning',
        action: 'VIEW_MEMBERS',
        actionLabel: 'View Members',
        meta: errorMeta,
      };

    case 'INVALID_EMAIL':
      return {
        title: 'Invalid email address',
        description: 'Please check the email address and try again.',
        variant: 'destructive',
        meta: errorMeta,
      };

    case 'INVITATION_EXPIRED':
      return {
        title: 'Invitation expired',
        description: 'This invitation has expired. Please create a new invitation.',
        variant: 'warning',
        action: 'CREATE_NEW',
        actionLabel: 'Create New Invitation',
        meta: errorMeta,
      };

    case 'INVITATION_ALREADY_ACCEPTED':
      return {
        title: 'Invitation already accepted',
        description: 'This invitation has already been accepted by the user.',
        variant: 'default',
        action: 'VIEW_MEMBERS',
        actionLabel: 'View Members',
        meta: errorMeta,
      };

    case 'INVITATION_NOT_FOUND':
      return {
        title: 'Invitation not found',
        description: 'The invitation you are looking for does not exist or has been deleted.',
        variant: 'destructive',
        action: 'CREATE_NEW',
        actionLabel: 'Create New Invitation',
        meta: errorMeta,
      };

    case 'NOT_WORKSPACE_MEMBER':
      return {
        title: 'Not a workspace member',
        description: 'You must be a member of this workspace to send invitations.',
        variant: 'destructive',
        action: 'JOIN_WORKSPACE',
        actionLabel: 'Join Workspace',
        meta: errorMeta,
      };

    case 'INSUFFICIENT_PERMISSIONS':
      return {
        title: 'Insufficient permissions',
        description: 'Only workspace owners and admins can send invitations.',
        variant: 'destructive',
        action: 'REQUEST_ACCESS',
        actionLabel: 'Request Access',
        meta: errorMeta,
      };

    case 'EMAIL_SEND_FAILED':
      return {
        title: 'Email could not be sent',
        description: errorMessage || 'The invitation was created but the email could not be sent. You can resend it.',
        variant: 'warning',
        action: 'RESEND',
        actionLabel: 'Resend Email',
        meta: errorMeta,
      };

    case 'CREATE_INVITATION_FAILED':
      return {
        title: 'Failed to create invitation',
        description: 'There was an error creating the invitation. Please try again.',
        variant: 'destructive',
        action: 'RETRY',
        actionLabel: 'Try Again',
        meta: errorMeta,
      };

    case 'UNAUTHORIZED':
      return {
        title: 'Authentication required',
        description: 'Please log in again to continue.',
        variant: 'destructive',
        meta: errorMeta,
      };

    case 'MISSING_FIELDS':
      return {
        title: 'Missing information',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
        meta: errorMeta,
      };

    case 'INTERNAL_ERROR':
    default:
      return {
        title: 'Something went wrong',
        description: errorMessage || 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
        action: 'RETRY',
        actionLabel: 'Try Again',
        meta: errorMeta,
      };
  }
}

/**
 * Checks if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  const errorCode = error?.code || error?.error?.code || '';
  const retryableCodes = [
    'EMAIL_SEND_FAILED',
    'CREATE_INVITATION_FAILED',
    'INTERNAL_ERROR',
  ];
  return retryableCodes.includes(errorCode);
}

